import { useState, useEffect } from 'react';
import { FileText, Calendar, Plus, X, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, Pencil, Trash2, Paperclip, Download, Upload, WifiOff } from 'lucide-react';
import { useAlarmReport } from '../contexts/AlarmReportContext';
import { apiFetch, apiUpload, apiFetchBlob, getUser } from '../api/client';
import { DataUnavailable } from './DataState';

// Format 'YYYY/MM/DD HH:MM:SS' Eastern timestamps for display.
function fmtEastern(ts: string | Date, showDate = true, showTime = true): string {
  const s = ts instanceof Date ? ts.toISOString().replace('T', ' ').substring(0, 19) : (ts || '');
  if (!s) return '';
  const [datePart, timePart = '00:00:00'] = s.split(' ');
  const sep = datePart.includes('/') ? '/' : '-';
  const [y, m, d] = datePart.split(sep).map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12  = hh % 12 || 12;
  if (showDate && showTime) return `${months[m-1]} ${d}, ${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
  if (showDate)             return `${months[m-1]} ${d}, ${y}`;
  return `${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
}

function severityLabel(s: string | undefined): string {
  return s === '1' ? '1 — Immediate Action' :
         s === '2' ? '2 — Schedule Action' :
         s === '3' ? '3 — Trend Closely' :
         s === '4' ? '4 — Trend Normally' :
         s === '5' ? '5 — No fault found, alarm adjusted' :
         s === '6' ? '6 — No fault found, no action taken' :
         (s || '—');
}

function severityColor(s: string | undefined): string {
  return s === '1' ? 'bg-red-100 text-red-700' :
         s === '2' ? 'bg-orange-100 text-orange-700' :
         s === '3' ? 'bg-yellow-100 text-yellow-700' :
         s === '4' ? 'bg-gray-100 text-gray-600' :
         s === '5' ? 'bg-blue-100 text-blue-700' :
         s === '6' ? 'bg-teal-100 text-teal-700' :
         'bg-gray-100 text-gray-600';
}

// Format UTC timestamps (report created_at) as Eastern.
// Appends Z if no timezone offset is present so the browser always treats the value as UTC.
function fmtUtcToEastern(ts: string, longMonth = false): string {
  if (!ts) return '';
  let s = ts.trim().replace(' ', 'T');
  if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    month: longMonth ? 'long' : 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Offline reports, plots and attachments
// ---------------------------------------------------------------------------

// Mirrors MAX_PLOTS in backend/routers/reports.py and the jsonb_array_length
// CHECK in migrate_offline_reports.py. Three statements of one number; the
// backend is the one that binds, this is the one that stops the analyst from
// finding out the hard way.
const MAX_PLOTS = 6;

// Longest edge, in px, a pasted plot is reduced to before encoding.
//
// NOT cosmetic. A base64 plot travels inside the report's JSON body, and six
// full-resolution Word screenshots comfortably exceed nginx's client_max_body_size
// — which fails as a 413 with no useful message, after the whole upload. A plot is
// line art; 1600px is past the point where more pixels are legible.
const MAX_PLOT_EDGE = 1600;

// Mirrors MAX_ATTACHMENT_BYTES in backend/routers/reports.py. Checked here so a
// too-large file fails instantly instead of after transferring 40 MB.
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

interface PlotImage {
  data_uri: string;
  caption?: string | null;
}

interface OfflineMachine {
  id: number;
  db_id: number;
  company_name: string;
  area_name: string;
  name: string;
}

interface Attachment {
  id: number;
  report_id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  sha256?: string | null;
  uploaded_by?: string | null;
  uploaded_at?: string | null;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Shrink an image data URI so its longest edge is at most MAX_PLOT_EDGE.
 *
 * Resolves to the ORIGINAL string on any failure (already small enough, canvas
 * unavailable, undecodable image). A plot that is bigger than ideal is a far
 * better outcome than a paste that silently produces nothing.
 */
function downscaleDataUri(dataUri: string): Promise<string> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const longest = Math.max(img.width, img.height);
      if (!longest || longest <= MAX_PLOT_EDGE) { resolve(dataUri); return; }
      const scale = MAX_PLOT_EDGE / longest;
      const canvas = document.createElement('canvas');
      canvas.width  = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(dataUri); return; }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      // PNG, not JPEG: these are line plots, where JPEG ringing around thin
      // strokes is exactly the artefact that makes a spectrum hard to read.
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUri);
    img.src = dataUri;
  });
}

function readFileAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

async function fileToPlot(file: File): Promise<PlotImage> {
  return { data_uri: await downscaleDataUri(await readFileAsDataUri(file)) };
}

/**
 * Pull images out of a paste or drop, and — when there are none — explain why.
 *
 * THE SILENT FAILURE THIS EXISTS TO KILL: the previous handler looked only at
 * `items` for `image/*`. Copying a picture OBJECT in Word puts a bitmap on the
 * clipboard and that works. Copying a SELECTION of Word content that contains a
 * chart does not: the clipboard carries text/html + text/rtf, with the image as
 * `<img src="file:///C:/Users/.../clip_image001.png">`. The browser cannot fetch
 * a file:// URL, so there is nothing to recover — the old code looped, matched
 * nothing, and did nothing at all. No image, no message, drop zone unchanged.
 *
 * Route work is exactly where analysts paste out of Word, so the second-best
 * outcome (tell them precisely what to do instead) has to be reachable.
 */
function extractPastedImages(dt: DataTransfer | null): { files: File[]; hint: string | null } {
  if (!dt) return { files: [], hint: 'Nothing was pasted.' };

  const files: File[] = [];
  // 1. items — how Chromium exposes a bitmap that an app put on the clipboard.
  for (const item of Array.from(dt.items || [])) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const f = item.getAsFile();
      if (f) files.push(f);
    }
  }
  // 2. files — some Office and file-manager paths populate this instead.
  if (!files.length) {
    for (const f of Array.from(dt.files || [])) {
      if (f.type.startsWith('image/')) files.push(f);
    }
  }
  if (files.length) return { files, hint: null };

  const types = Array.from(dt.types || []);
  if (types.includes('text/html') || types.includes('text/rtf')) {
    return {
      files: [],
      hint: 'That looks like a copied Word or Outlook selection. It carries the chart as a link to a file on your PC, not as an image, so there is nothing to paste. In Word, right-click the chart itself → Copy (or Save as Picture), then paste or drop it here.',
    };
  }
  if (types.includes('text/plain')) {
    return { files: [], hint: 'That paste was text, not an image. Copy the chart itself, or drop a PNG/JPG file here.' };
  }
  return { files: [], hint: 'No image found on the clipboard. Copy the chart itself, or drop a PNG/JPG file here.' };
}

interface Report {
  id: string;
  machineId: string;
  machine: string;
  area: string;
  plant: string;
  dbName: string;
  companyName: string;
  date: string;
  severity?: string;
  analysis?: string;
  author?: string;
  observations?: string;
  recommendations?: string;
  // 'offline' = a manual-route asset with no wireless sensor. It has no machine
  // detail page, so the machine name must never render as a link for one.
  kind: 'online' | 'offline';
  offlineMachineId?: number | null;
  plotImages: PlotImage[];
  attachmentCount: number;
}

interface AlarmEvent {
  id: string;
  pointName: string;
  metric: string;
  value: number;
  threshold: number;
  severity: 'medium' | 'high';
  timestamp: Date;
  description: string;
}

interface ReportsProps {
  selectedReportId?: string | null;
  prefillAlarm?: { alarmId: string; machineName: string; metric: string } | null;
  onMachineClick?: (machineId: string, machineName: string, points: never[], imageUrl?: string) => void;
}

// API shapes
interface ApiReport {
  id: number;
  machine_id: string;
  machine_name: string;
  company_name: string;
  area_name: string;
  db_id: number;
  db_name?: string;
  severity: number;
  analysis_type: string;
  observations: string | null;
  recommendations: string | null;
  status: string;
  created_by: string;
  created_at: string;
  linked_alarm_id: number | null;
  report_kind?: string | null;
  offline_machine_id?: number | null;
  // Detail route only — the list route omits plot payloads on purpose.
  plot_images?: PlotImage[] | null;
  attachment_count?: number | null;
}

interface ApiMachineItem {
  id: string;
  name: string;
  db_id: number;
  db_name: string;
  company_name: string;
  area_name: string;
}

function mapApiReport(r: ApiReport): Report {
  return {
    id: String(r.id),
    machineId: String(r.machine_id),
    machine: r.machine_name,
    area: r.area_name,
    plant: r.company_name,
    dbName: r.db_name || '',
    companyName: r.company_name,
    date: (r.created_at || '').substring(0, 10),
    severity: String(r.severity),
    analysis: r.analysis_type,
    author: r.created_by,
    observations: r.observations || '',
    recommendations: r.recommendations || '',
    kind: r.report_kind === 'offline' ? 'offline' : 'online',
    offlineMachineId: r.offline_machine_id ?? null,
    plotImages: r.plot_images || [],
    attachmentCount: r.attachment_count ?? 0,
  };
}

const ANALYSIS_TYPES = [
  'Bearing', 'Belt/Sheave', 'Coupling', 'Eccentricity', 'Electrical',
  'Electrical Fluting', 'Lubrication', 'Mechanical Looseness', 'Structural Looseness',
  'Unbalance', 'Mis-Alignment', 'Unknown', 'See Comments',
];

export function Reports({ selectedReportId, prefillAlarm, onMachineClick }: ReportsProps) {
  const isAdmin = getUser()?.role === 'admin';
  const { linkAlarmToReport } = useAlarmReport();

  // Filter state — hierarchy drill-down
  const [filterDb, setFilterDb]         = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterArea, setFilterArea]     = useState('');
  const [filterMachine, setFilterMachine] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('All');

  const [sortColumn, setSortColumn]     = useState<'date' | 'severity'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Modal state
  const [isNewReportOpen, setIsNewReportOpen] = useState(false);
  const [viewingReport, setViewingReport]     = useState<Report | null>(null);
  const [editingReport, setEditingReport]     = useState<Report | null>(null);
  const [deleteTarget, setDeleteTarget]       = useState<Report | null>(null);

  // New/edit form state
  const [formState, setFormState] = useState({
    // 'online'  -> the four cascading selects, driven by /machines
    // 'offline' -> a manual-route asset, typed or picked from /offline-machines
    kind: 'online' as 'online' | 'offline',
    plant: '', company: '', area: '', machine: '',
    offlineCompany: '', offlineArea: '', offlineMachine: '',
    severity: '1', analysis: '', alarm: '',
    observations: '', recommendations: '',
    plotImages: [] as PlotImage[],
  });
  // PDFs chosen but not yet uploaded. They cannot be sent with the report: an
  // attachment needs a report id, so create is two-phase (report, then files).
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  // Attachments already on the report being edited or viewed.
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [selectedAlarm, setSelectedAlarm] = useState<AlarmEvent | null>(null);
  // One submit at a time. Without this the Create button stayed live for the whole
  // ~2s round trip (a pasted plot is ~300 KB), and on 2026-08-05 three clicks made
  // three reports and three emails to five recipients.
  const [isSaving, setIsSaving] = useState(false);

  // API data
  const [reports, setReports]           = useState<Report[]>([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportsError, setReportsError] = useState(false);
  const [apiMachines, setApiMachines]   = useState<ApiMachineItem[]>([]);
  const [machinesError, setMachinesError] = useState(false);
  const [apiAlarms, setApiAlarms]       = useState<AlarmEvent[]>([]);
  const [alarmsError, setAlarmsError]   = useState(false);
  const [offlineMachines, setOfflineMachines] = useState<OfflineMachine[]>([]);

  // Load reports
  useEffect(() => {
    setReportsLoading(true);
    setReportsError(false);
    apiFetch<{ reports: ApiReport[] }>('/reports?limit=500')
      .then(data => { setReports((data.reports || []).map(mapApiReport)); setReportsError(false); })
      .catch(() => { setReports([]); setReportsError(true); })
      .finally(() => setReportsLoading(false));
  }, []);

  // Load machines for form dropdowns
  useEffect(() => {
    apiFetch<ApiMachineItem[]>('/machines')
      .then(data => { setApiMachines(data); setMachinesError(false); })
      .catch(() => { setApiMachines([]); setMachinesError(true); });
  }, []);

  // Load the offline (manual route) hierarchy. Feeds the offline form's
  // suggestions and the filter bar. A failure here is not fatal: the form falls
  // back to free text, which the idempotent POST /offline-machines resolves to
  // the same row anyway.
  useEffect(() => {
    apiFetch<OfflineMachine[]>('/offline-machines')
      .then(setOfflineMachines)
      .catch(() => setOfflineMachines([]));
  }, []);

  // Load alarms when machine selection changes (form)
  useEffect(() => {
    if (!formState.machine) { setApiAlarms([]); return; }
    const machine = apiMachines.find(m => m.name === formState.machine);
    if (!machine) { setApiAlarms([]); return; }
    setAlarmsError(false);
    apiFetch<{ events: any[] }>(`/alarms/history?machine_id=${machine.id}&limit=50`)
      .then(data => {
        setApiAlarms((data.events || []).map((e: any) => ({
          id: String(e.id),
          pointName: e.point_name || '',
          metric: (e.unit_label || '').replace(/mm\/s/g, 'in/s'),
          value: e.peak_value ?? 0,
          threshold: e.threshold ?? 0,
          severity: e.severity >= 3 ? 'high' : 'medium',
          timestamp: new Date((e.measurement_at || '').replace(/\//g, '-').replace(' ', 'T')),
          description: `${(e.unit_label || '').replace(/mm\/s/g, 'in/s')} exceeded threshold at ${e.point_name}`,
        })));
        setAlarmsError(false);
      })
      .catch(() => { setApiAlarms([]); setAlarmsError(true); });
  }, [formState.machine, apiMachines]);

  // Auto-open new report from alarm
  useEffect(() => {
    if (!prefillAlarm) return;
    setFormState(prev => ({ ...prev, alarm: prefillAlarm.alarmId, machine: prefillAlarm.machineName }));
    setIsNewReportOpen(true);
  }, [prefillAlarm]);

  // Opening the view modal pulls the plots and attachments the list route omits.
  useEffect(() => {
    if (!viewingReport) { return; }
    loadReportDetail(viewingReport.id, false);
    // Keyed on the id, not the object: setViewingReport is called again below with
    // the loaded plots, and depending on the object would re-fetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingReport?.id]);

  // Auto-open report by id
  useEffect(() => {
    if (selectedReportId && reports.length > 0) {
      const r = reports.find(r => r.id === selectedReportId);
      if (r) setViewingReport(r);
    }
  }, [selectedReportId, reports]);

  // ---------------------------------------------------------------------------
  // Derived filter lists (cascade)
  // ---------------------------------------------------------------------------
  // Database name <-> id. /machines carries both; offline_machines carries only
  // the id, so this is how an offline row gets a label and how the offline form
  // turns the picked database name back into the id the API wants.
  const dbIdForName = (name: string): number | null =>
    apiMachines.find(m => m.db_name === name)?.db_id ?? null;
  const dbNameForId = (id: number): string =>
    apiMachines.find(m => m.db_id === id)?.db_name ?? `DB ${id}`;

  // Filter options are the union of the ONLINE hierarchy (/machines) and the
  // OFFLINE one (/offline-machines). Deriving them from /machines alone — as this
  // did — means an offline report's company/area/machine never appears as an
  // option, so any filter selection makes every offline report vanish from a
  // table that was showing it a moment ago.
  const offlineRows = offlineMachines.map(o => ({
    db_name: dbNameForId(o.db_id),
    company_name: o.company_name,
    area_name: o.area_name,
    name: o.name,
  }));
  const allRows = [
    ...apiMachines.map(m => ({
      db_name: m.db_name, company_name: m.company_name,
      area_name: m.area_name, name: m.name,
    })),
    ...offlineRows,
  ];

  const dbNames     = [...new Set(allRows.map(m => m.db_name).filter(Boolean))].sort();
  const companies   = [...new Set(allRows
    .filter(m => !filterDb || m.db_name === filterDb)
    .map(m => m.company_name)
    .filter(c => c && c !== (filterDb || ''))
  )].sort();
  const areas       = [...new Set(allRows
    .filter(m => (!filterDb || m.db_name === filterDb) && (!filterCompany || m.company_name === filterCompany))
    .map(m => m.area_name)
  )].sort();
  const machineNames = [...new Set(allRows
    .filter(m =>
      (!filterDb      || m.db_name      === filterDb) &&
      (!filterCompany || m.company_name === filterCompany) &&
      (!filterArea    || m.area_name    === filterArea)
    )
    .map(m => m.name)
  )].sort();

  // ---------------------------------------------------------------------------
  // Filtered + sorted reports
  // ---------------------------------------------------------------------------
  const filteredReports = reports
    .filter(r => {
      if (filterDb      && r.dbName      !== filterDb)      return false;
      if (filterCompany && r.companyName !== filterCompany) return false;
      if (filterArea    && r.area        !== filterArea)     return false;
      if (filterMachine && r.machine     !== filterMachine)  return false;
      if (filterSeverity !== 'All' && r.severity !== filterSeverity) return false;
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortColumn === 'date') cmp = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (sortColumn === 'severity') cmp = parseInt(a.severity || '0') - parseInt(b.severity || '0');
      return sortDirection === 'asc' ? cmp : -cmp;
    });

  const handleSort = (col: 'date' | 'severity') => {
    if (sortColumn === col) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(col); setSortDirection('desc'); }
  };

  // ---------------------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------------------
  const resetForm = () => {
    setFormState({
      kind: 'online',
      plant: '', company: '', area: '', machine: '',
      offlineCompany: '', offlineArea: '', offlineMachine: '',
      severity: '1', analysis: '', alarm: '',
      observations: '', recommendations: '', plotImages: [],
    });
    setSelectedAlarm(null);
    setPendingFiles([]);
    setAttachments([]);
  };


  // Offline suggestions, narrowed by what has been chosen so far.
  const offlineForDb = formState.plant
    ? offlineMachines.filter(o => dbNameForId(o.db_id) === formState.plant)
    : offlineMachines;
  const offlineCompanies = [...new Set(offlineForDb.map(o => o.company_name))].sort();
  const offlineAreas = [...new Set(offlineForDb
    .filter(o => !formState.offlineCompany || o.company_name === formState.offlineCompany)
    .map(o => o.area_name))].sort();
  const offlineMachineNames = [...new Set(offlineForDb
    .filter(o => (!formState.offlineCompany || o.company_name === formState.offlineCompany)
              && (!formState.offlineArea || o.area_name === formState.offlineArea))
    .map(o => o.name))].sort();

  const formMachines = apiMachines.filter(m =>
    (!formState.plant   || m.db_name      === formState.plant) &&
    (!formState.company || m.company_name === formState.company) &&
    (!formState.area    || m.area_name    === formState.area)
  );

  const formCompanies = [...new Set(apiMachines
    .filter(m => !formState.plant || m.db_name === formState.plant)
    .map(m => m.company_name).filter(c => c && c !== formState.plant)
  )];
  const showCompany = formCompanies.length > 0;

  const formAreas = [...new Set(apiMachines
    .filter(m => (!formState.plant || m.db_name === formState.plant) && (!formState.company || m.company_name === formState.company))
    .map(m => m.area_name)
  )];

  // ---------------------------------------------------------------------------
  // CRUD
  // ---------------------------------------------------------------------------
  /**
   * Upload the queued PDFs to a saved report.
   *
   * One request per file, and a failure is collected rather than thrown: the
   * report is already committed by this point, so aborting would leave the
   * analyst with a saved report and an error that looks like the report failed.
   * Returns the names that did not make it.
   */
  const uploadPendingFiles = async (reportId: string, files: File[]): Promise<string[]> => {
    const failed: string[] = [];
    for (const f of files) {
      try {
        const form = new FormData();
        form.append('file', f);
        await apiUpload<Attachment>(`/reports/${reportId}/attachments`, form);
      } catch {
        failed.push(f.name);
      }
    }
    return failed;
  };

  const handleCreateReport = async () => {
    if (isSaving) return;   // second click during the round trip = same report, twice
    if (!formState.analysis) { alert('Please select an analysis type.'); return; }

    const offline = formState.kind === 'offline';
    let machineId: string | null = null;
    let offlineMachineId: number | null = null;

    if (offline) {
      const dbId = dbIdForName(formState.plant);
      if (dbId === null) { alert('Please select a database.'); return; }
      if (!formState.offlineCompany.trim() || !formState.offlineArea.trim()
          || !formState.offlineMachine.trim()) {
        alert('Please fill in Company, Area and Machine.');
        return;
      }
    } else {
      const machine = apiMachines.find(m => m.name === formState.machine);
      if (!machine) { alert('Please select a machine.'); return; }
      machineId = String(machine.id);
    }

    setIsSaving(true);
    try {
      if (offline) {
        // Resolve the typed hierarchy to a row FIRST. The endpoint is idempotent
        // on (db_id, company, area, name), so an asset that already exists comes
        // back rather than erroring — which is what makes free typing safe.
        const om = await apiFetch<OfflineMachine>('/offline-machines', {
          method: 'POST',
          body: JSON.stringify({
            db_id: dbIdForName(formState.plant),
            company_name: formState.offlineCompany.trim(),
            area_name: formState.offlineArea.trim(),
            name: formState.offlineMachine.trim(),
          }),
        });
        offlineMachineId = om.id;
        setOfflineMachines(prev =>
          prev.some(o => o.id === om.id) ? prev : [...prev, om]);
      }

      const payload = {
        report_kind: offline ? 'offline' : 'online',
        machine_id: machineId,
        offline_machine_id: offlineMachineId,
        severity: Math.min(6, Math.max(1, parseInt(formState.severity) || 1)),
        analysis_type: formState.analysis,
        observations: formState.observations || null,
        recommendations: formState.recommendations || null,
        // An offline asset has no sensor, so it has no alarms to link to.
        linked_alarm_id: (!offline && formState.alarm) ? parseInt(formState.alarm) : null,
        plot_images: formState.plotImages,
      };

      const created = await apiFetch<ApiReport>('/reports', { method: 'POST', body: JSON.stringify(payload) });
      const mapped = mapApiReport(created);

      // Phase two. The report exists now, so a failed upload costs one file, not
      // the whole report.
      const queued = pendingFiles;
      const failed = await uploadPendingFiles(mapped.id, queued);
      mapped.attachmentCount = queued.length - failed.length;

      setReports(prev => [mapped, ...prev]);
      if (!offline && formState.alarm) linkAlarmToReport(formState.alarm, mapped.id);
      resetForm();
      setIsNewReportOpen(false);
      if (failed.length) {
        alert(`The report was saved, but ${failed.length} attachment(s) failed to upload: `
              + `${failed.join(', ')}. You can add them again by editing the report.`);
      }
    } catch (err: any) {
      alert('Failed to save report: ' + (err?.message || JSON.stringify(err)));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateReport = async () => {
    if (!editingReport) return;
    if (isSaving) return;
    const payload = {
      severity: parseInt(formState.severity) || 1,
      analysis_type: formState.analysis,
      observations: formState.observations || null,
      recommendations: formState.recommendations || null,
      plot_images: formState.plotImages,
    };
    setIsSaving(true);
    try {
      const updated = await apiFetch<ApiReport>(`/reports/${editingReport.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      const mapped = mapApiReport(updated);
      const failed = await uploadPendingFiles(mapped.id, pendingFiles);
      mapped.attachmentCount = attachments.length + pendingFiles.length - failed.length;
      setReports(prev => prev.map(r => r.id === mapped.id ? mapped : r));
      if (viewingReport?.id === mapped.id) setViewingReport(mapped);
      setEditingReport(null);
      resetForm();
      if (failed.length) {
        alert(`The report was saved, but ${failed.length} attachment(s) failed to upload: `
              + `${failed.join(', ')}.`);
      }
    } catch (err: any) {
      alert('Failed to update report: ' + (err?.message || JSON.stringify(err)));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteReport = async () => {
    if (!deleteTarget) return;
    try {
      await apiFetch(`/reports/${deleteTarget.id}`, { method: 'DELETE' });
      setReports(prev => prev.filter(r => r.id !== deleteTarget.id));
      if (viewingReport?.id === deleteTarget.id) setViewingReport(null);
      setDeleteTarget(null);
    } catch (err: any) {
      alert('Failed to delete report: ' + (err?.message || JSON.stringify(err)));
    }
  };

  const openEdit = (report: Report) => {
    const offline = report.kind === 'offline';
    setFormState({
      kind: report.kind,
      plant: report.dbName || '',
      company: offline ? '' : (report.companyName || ''),
      area: offline ? '' : (report.area || ''),
      machine: offline ? '' : (report.machine || ''),
      offlineCompany: offline ? (report.companyName || '') : '',
      offlineArea: offline ? (report.area || '') : '',
      offlineMachine: offline ? (report.machine || '') : '',
      severity: report.severity || '1',
      analysis: report.analysis || '',
      alarm: '',
      observations: report.observations || '',
      recommendations: report.recommendations || '',
      plotImages: [],
    });
    setPendingFiles([]);
    setAttachments([]);
    setEditingReport(report);
    // The list route omits plot payloads, so the row in `reports` has none. Fetch
    // the full record before editing, or saving would wipe every plot on it.
    loadReportDetail(report.id);
  };

  /**
   * Pull the plots and attachments for one report.
   *
   * Both the edit and view modals need them and neither can get them from the
   * list: /reports omits plot payloads (six base64 images x 500 rows is a
   * multi-megabyte response for a table that renders none of them).
   */
  const loadReportDetail = (id: string, intoForm = true) => {
    apiFetch<ApiReport>(`/reports/${id}`)
      .then(full => {
        const plots = full.plot_images || [];
        if (intoForm) setFormState(prev => ({ ...prev, plotImages: plots }));
        setReports(prev => prev.map(r => r.id === id ? { ...r, plotImages: plots } : r));
        setViewingReport(prev => prev && prev.id === id ? { ...prev, plotImages: plots } : prev);
      })
      .catch(() => { /* plots stay empty; the report itself still edits */ });
    apiFetch<Attachment[]>(`/reports/${id}/attachments`)
      .then(setAttachments)
      .catch(() => setAttachments([]));
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-gray-900 flex items-center gap-3">
              <FileText className="w-8 h-8 text-gray-700" />
              Published Reports
            </h1>
            <p className="text-gray-600 mt-2">All published reports related to machinery in the database</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-gray-900 px-4 py-2 bg-white rounded-lg border border-gray-200">
              {filteredReports.length} Reports
            </div>
            {isAdmin && (
              <button
                onClick={() => { resetForm(); setIsNewReportOpen(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                New Report
              </button>
            )}
          </div>
        </div>

        {/* Hierarchy filter dropdowns */}
        <div className="flex gap-3 flex-wrap">
          <select value={filterDb} onChange={e => { setFilterDb(e.target.value); setFilterCompany(''); setFilterArea(''); setFilterMachine(''); }}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm">
            <option value="">All Databases</option>
            {dbNames.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filterCompany} onChange={e => { setFilterCompany(e.target.value); setFilterArea(''); setFilterMachine(''); }}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm">
            <option value="">All Companies</option>
            {companies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterArea} onChange={e => { setFilterArea(e.target.value); setFilterMachine(''); }}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm">
            <option value="">All Areas</option>
            {areas.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filterMachine} onChange={e => setFilterMachine(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm">
            <option value="">All Machines</option>
            {machineNames.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm">
            <option value="All">All Severities</option>
            <option value="1">1 — Immediate Action</option>
            <option value="2">2 — Schedule Action</option>
            <option value="3">3 — Trend Closely</option>
            <option value="4">4 — Trend Normally</option>
            <option value="5">5 — No fault found, alarm adjusted</option>
            <option value="6">6 — No fault found, no action taken</option>
          </select>
          {(filterDb || filterCompany || filterArea || filterMachine || filterSeverity !== 'All') && (
            <button onClick={() => { setFilterDb(''); setFilterCompany(''); setFilterArea(''); setFilterMachine(''); setFilterSeverity('All'); }}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Machines could not be loaded — filters/form dropdowns will be empty; say so. */}
      {machinesError && (
        <DataUnavailable message="Machine list could not be loaded — filters and the report form may be incomplete." compact />
      )}

      {/* Reports Table */}
      {reportsLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center text-gray-400">Loading reports…</div>
      ) : reportsError ? (
        <DataUnavailable message="Reports could not be loaded." />
      ) : filteredReports.length > 0 ? (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-gray-700">
                    <button onClick={() => handleSort('date')} className="flex items-center gap-1 hover:text-gray-900 transition-colors">
                      Date
                      {sortColumn === 'date' ? (sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />) : <ArrowUpDown className="w-4 h-4 text-gray-400" />}
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-gray-700">Machine</th>
                  <th className="px-6 py-4 text-left text-gray-700">Location</th>
                  <th className="px-6 py-4 text-left text-gray-700">
                    <button onClick={() => handleSort('severity')} className="flex items-center gap-1 hover:text-gray-900 transition-colors">
                      Severity
                      {sortColumn === 'severity' ? (sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />) : <ArrowUpDown className="w-4 h-4 text-gray-400" />}
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-gray-700">Analysis</th>
                  <th className="px-6 py-4 text-left text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 text-gray-600 text-sm whitespace-nowrap">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {fmtUtcToEastern(report.date)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {/* An offline asset has no machine detail page, so the name
                            is NOT a link. The badge answers "why can't I click
                            this?" before it is asked. */}
                        {onMachineClick && report.kind === 'online' ? (
                          <button
                            onClick={() => onMachineClick(report.machineId, report.machine, [], undefined)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-sm transition-colors text-left"
                          >
                            {report.machine}
                          </button>
                        ) : (
                          <span className="text-gray-900 font-medium text-sm">{report.machine}</span>
                        )}
                        {report.kind === 'offline' && (
                          <span
                            title="Offline report — manual route data, no wireless sensor"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200"
                          >
                            <WifiOff className="w-3 h-3" />
                            Offline
                          </span>
                        )}
                        {report.attachmentCount > 0 && (
                          <span
                            title={`${report.attachmentCount} PDF attachment(s)`}
                            className="inline-flex items-center gap-0.5 text-gray-400"
                          >
                            <Paperclip className="w-3.5 h-3.5" />
                            <span className="text-[10px]">{report.attachmentCount}</span>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{report.companyName} → {report.area}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${severityColor(report.severity)}`}>
                        {severityLabel(report.severity)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{report.analysis}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setViewingReport(report)}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          View
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => openEdit(report)}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Edit report"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(report)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete report"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-12 border border-gray-200 shadow-sm text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-gray-900 mb-2">No reports found</h3>
          <p className="text-gray-600">Try adjusting your filter criteria</p>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* New Report Modal                                                     */}
      {/* ------------------------------------------------------------------ */}
      {isNewReportOpen && (
        <ReportFormModal
          title="Create New Report"
          formState={formState}
          setFormState={setFormState}
          apiMachines={apiMachines}
          formMachines={formMachines}
          formCompanies={formCompanies}
          formAreas={formAreas}
          showCompany={showCompany}
          apiAlarms={apiAlarms}
          apiAlarmsError={alarmsError}
          selectedAlarm={selectedAlarm}
          setSelectedAlarm={setSelectedAlarm}
          offlineCompanies={offlineCompanies}
          offlineAreas={offlineAreas}
          offlineMachineNames={offlineMachineNames}
          attachments={attachments}
          setAttachments={setAttachments}
          pendingFiles={pendingFiles}
          setPendingFiles={setPendingFiles}
          reportId={null}
          onSubmit={handleCreateReport}
          onCancel={() => { setIsNewReportOpen(false); resetForm(); }}
          submitLabel="Create Report"
          submitting={isSaving}
        />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Edit Report Modal                                                    */}
      {/* ------------------------------------------------------------------ */}
      {editingReport && (
        <ReportFormModal
          title={`Edit Report — ${editingReport.machine}`}
          formState={formState}
          setFormState={setFormState}
          apiMachines={apiMachines}
          formMachines={formMachines}
          formCompanies={formCompanies}
          formAreas={formAreas}
          showCompany={showCompany}
          apiAlarms={apiAlarms}
          apiAlarmsError={alarmsError}
          selectedAlarm={selectedAlarm}
          setSelectedAlarm={setSelectedAlarm}
          offlineCompanies={offlineCompanies}
          offlineAreas={offlineAreas}
          offlineMachineNames={offlineMachineNames}
          attachments={attachments}
          setAttachments={setAttachments}
          pendingFiles={pendingFiles}
          setPendingFiles={setPendingFiles}
          reportId={editingReport.id}
          onSubmit={handleUpdateReport}
          onCancel={() => { setEditingReport(null); resetForm(); }}
          submitLabel="Save Changes"
          editMode
          submitting={isSaving}
        />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* View Report Modal                                                    */}
      {/* ------------------------------------------------------------------ */}
      {viewingReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-gray-900 flex items-center gap-3">
                <FileText className="w-6 h-6 text-gray-700" />
                {viewingReport.machine}
              </h2>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <>
                    <button onClick={() => { openEdit(viewingReport); setViewingReport(null); }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setDeleteTarget(viewingReport); setViewingReport(null); }}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-400 hover:text-red-600" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button onClick={() => setViewingReport(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Database</label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm">{viewingReport.dbName || '—'}</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Company / Area</label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm">{viewingReport.companyName} → {viewingReport.area}</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Machine</label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm">
                    {onMachineClick && viewingReport.kind === 'online' ? (
                      <button onClick={() => { setViewingReport(null); onMachineClick(viewingReport.machineId, viewingReport.machine, [], undefined); }}
                        className="text-blue-600 hover:underline font-medium">
                        {viewingReport.machine}
                      </button>
                    ) : (
                      <span className="text-gray-900 flex items-center gap-2">
                        {viewingReport.machine}
                        {viewingReport.kind === 'offline' && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                            <WifiOff className="w-3 h-3" />
                            Offline
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Severity</label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm">
                    {severityLabel(viewingReport.severity)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Analysis</label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm">{viewingReport.analysis || '—'}</div>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Observations</label>
                <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm min-h-[80px] whitespace-pre-wrap">
                  {viewingReport.observations || 'No observations provided.'}
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Recommendations</label>
                <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm min-h-[80px] whitespace-pre-wrap">
                  {viewingReport.recommendations || 'No recommendations provided.'}
                </div>
              </div>
              {viewingReport.plotImages.length > 0 && (
                <div>
                  <label className="block text-sm text-gray-500 mb-1">
                    {viewingReport.plotImages.length === 1 ? 'Plot' : `Plots (${viewingReport.plotImages.length})`}
                  </label>
                  <div className="space-y-3">
                    {viewingReport.plotImages.map((plot, i) => (
                      <div key={i}>
                        <img src={plot.data_uri} alt={`Report plot ${i + 1}`} className="w-full rounded-lg border border-gray-200 bg-gray-50 object-contain max-h-96" />
                        {plot.caption && <p className="text-xs text-gray-500 mt-1">{plot.caption}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {attachments.length > 0 && (
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Attachments</label>
                  <div className="space-y-2">
                    {attachments.map(att => (
                      <AttachmentRow key={att.id} reportId={viewingReport.id} attachment={att} />
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs text-gray-400 pt-1">Created by {viewingReport.author} on {fmtUtcToEastern(viewingReport.date, true)}</div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation Modal                                            */}
      {/* ------------------------------------------------------------------ */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-gray-900 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                Delete Report
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700">Are you sure you want to permanently delete the report for <span className="font-semibold">{deleteTarget.machine}</span>? This cannot be undone.</p>
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleDeleteReport} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One attachment, in the view modal
// ---------------------------------------------------------------------------

/**
 * A download link that carries the bearer token.
 *
 * A plain <a href> cannot be used: the endpoint is authenticated and an anchor
 * sends no Authorization header, so the browser would get a 401 and render the
 * JSON error as a "downloaded" file.
 */
function AttachmentRow({ reportId, attachment }: { reportId: string; attachment: Attachment }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    let url: string | null = null;
    try {
      const blob = await apiFetchBlob(`/reports/${reportId}/attachments/${attachment.id}`);
      url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err: any) {
      setError(err?.message || 'Download failed');
    } finally {
      // Revoked on a timer, not immediately: Chrome needs the URL to still
      // resolve when it starts the download a tick after the click.
      if (url) setTimeout(() => URL.revokeObjectURL(url as string), 30_000);
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
      <div className="flex items-center gap-2 min-w-0">
        <Paperclip className="w-4 h-4 text-gray-400 shrink-0" />
        <span className="text-sm text-gray-800 truncate" title={attachment.filename}>
          {attachment.filename}
        </span>
        <span className="text-xs text-gray-400 shrink-0">{formatBytes(attachment.size_bytes)}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {error && <span className="text-xs text-red-600">{error}</span>}
        <button
          onClick={download}
          disabled={busy}
          className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {busy ? 'Downloading…' : 'Download'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plots — paste, drop or pick, up to MAX_PLOTS
// ---------------------------------------------------------------------------

function PlotEditor({
  plots, setPlots,
}: { plots: PlotImage[]; setPlots: (p: PlotImage[]) => void }) {
  const [hint, setHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const addFiles = async (files: File[]) => {
    if (!files.length) return;
    const room = MAX_PLOTS - plots.length;
    if (room <= 0) { setHint(`A report can hold at most ${MAX_PLOTS} plots.`); return; }
    const taking = files.slice(0, room);
    setBusy(true);
    try {
      const added = await Promise.all(taking.map(fileToPlot));
      setPlots([...plots, ...added]);
      setHint(files.length > room
        ? `Added ${room}; a report can hold at most ${MAX_PLOTS} plots.`
        : null);
    } catch (err: any) {
      setHint(err?.message || 'That image could not be read.');
    } finally {
      setBusy(false);
    }
  };

  const onPasteOrDrop = (dt: DataTransfer | null) => {
    const { files, hint: why } = extractPastedImages(dt);
    if (files.length) { setHint(null); addFiles(files); }
    else setHint(why);   // never silent — see extractPastedImages
  };

  return (
    <div>
      <label className="block text-gray-700 mb-2 text-sm">
        Plots (optional) — {plots.length}/{MAX_PLOTS}
      </label>

      {plots.length > 0 && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          {plots.map((plot, i) => (
            <div key={i} className="relative border border-gray-200 rounded-lg overflow-hidden">
              <img src={plot.data_uri} alt={`Plot ${i + 1}`} className="w-full h-32 object-contain bg-gray-50" />
              <button
                type="button"
                onClick={() => setPlots(plots.filter((_, j) => j !== i))}
                className="absolute top-1 right-1 p-1 bg-white rounded-full shadow hover:bg-red-50 transition-colors"
                title="Remove plot"
              >
                <X className="w-3.5 h-3.5 text-red-500" />
              </button>
              <input
                value={plot.caption || ''}
                onChange={e => setPlots(plots.map((pl, j) =>
                  j === i ? { ...pl, caption: e.target.value } : pl))}
                placeholder="Caption (optional)"
                className="w-full px-2 py-1 text-xs border-t border-gray-200 focus:outline-none"
              />
            </div>
          ))}
        </div>
      )}

      {plots.length < MAX_PLOTS && (
        <div
          tabIndex={0}
          onPaste={e => { e.preventDefault(); onPasteOrDrop(e.clipboardData); }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); onPasteOrDrop(e.dataTransfer); }}
          className="w-full px-3 py-6 border-2 border-dashed border-gray-200 rounded-lg text-center text-sm text-gray-400 focus:outline-none focus:border-blue-400 hover:border-gray-300 transition-colors"
        >
          <div className="flex flex-col items-center gap-1">
            <span>{busy ? 'Processing…' : 'Click here and paste, or drop an image'}</span>
            <span className="text-xs text-gray-300">Ctrl+V / Cmd+V — or</span>
            <label className="text-xs text-blue-600 hover:underline cursor-pointer">
              choose a file
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={e => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
              />
            </label>
          </div>
        </div>
      )}

      {/* The paste that produces nothing is the one worth explaining. */}
      {hint && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{hint}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF attachments in the form
// ---------------------------------------------------------------------------

function AttachmentEditor({
  reportId, existing, setExisting, pending, setPending,
}: {
  reportId: string | null;
  existing: Attachment[];
  setExisting: (a: Attachment[]) => void;
  pending: File[];
  setPending: (f: File[]) => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const addFiles = (files: File[]) => {
    const good: File[] = [];
    const bad: string[] = [];
    for (const f of files) {
      // Both checks are advisory duplicates of the server's — they exist so a
      // wrong file fails instantly instead of after transferring 40 MB. The
      // server still re-checks, on the bytes rather than the name.
      if (f.size > MAX_ATTACHMENT_BYTES) {
        bad.push(`${f.name} is ${formatBytes(f.size)} (limit ${formatBytes(MAX_ATTACHMENT_BYTES)})`);
      } else if (!/\.pdf$/i.test(f.name) && f.type !== 'application/pdf') {
        bad.push(`${f.name} is not a PDF`);
      } else {
        good.push(f);
      }
    }
    setError(bad.length ? bad.join('; ') : null);
    if (good.length) setPending([...pending, ...good]);
  };

  const removeExisting = async (att: Attachment) => {
    if (!reportId) return;
    try {
      await apiFetch(`/reports/${reportId}/attachments/${att.id}`, { method: 'DELETE' });
      setExisting(existing.filter(a => a.id !== att.id));
    } catch (err: any) {
      setError(err?.message || 'Could not remove the attachment');
    }
  };

  return (
    <div>
      <label className="block text-gray-700 mb-2 text-sm">PDF attachments (optional)</label>

      {existing.length > 0 && (
        <div className="space-y-2 mb-2">
          {existing.map(att => (
            <div key={att.id} className="flex items-center justify-between gap-3 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center gap-2 min-w-0">
                <Paperclip className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-sm text-gray-800 truncate" title={att.filename}>{att.filename}</span>
                <span className="text-xs text-gray-400 shrink-0">{formatBytes(att.size_bytes)}</span>
              </div>
              <button
                type="button"
                onClick={() => removeExisting(att)}
                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors shrink-0"
                title="Remove attachment"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {pending.length > 0 && (
        <div className="space-y-2 mb-2">
          {pending.map((f, i) => (
            <div key={`${f.name}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2 border border-blue-200 rounded-lg bg-blue-50">
              <div className="flex items-center gap-2 min-w-0">
                <Upload className="w-4 h-4 text-blue-400 shrink-0" />
                <span className="text-sm text-gray-800 truncate" title={f.name}>{f.name}</span>
                <span className="text-xs text-gray-400 shrink-0">{formatBytes(f.size)}</span>
                <span className="text-[10px] text-blue-600 shrink-0">will upload on save</span>
              </div>
              <button
                type="button"
                onClick={() => setPending(pending.filter((_, j) => j !== i))}
                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors shrink-0"
                title="Remove"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(Array.from(e.dataTransfer?.files || [])); }}
        className="w-full px-3 py-4 border-2 border-dashed border-gray-200 rounded-lg text-center text-sm text-gray-400 hover:border-gray-300 transition-colors"
      >
        <label className="cursor-pointer">
          Drop a PDF here, or <span className="text-blue-600 hover:underline">choose a file</span>
          <input
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={e => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
          />
        </label>
      </div>

      {error && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared form modal (used by both Create and Edit)
// ---------------------------------------------------------------------------

interface ReportFormModalProps {
  title: string;
  formState: any;
  setFormState: (s: any) => void;
  apiMachines: ApiMachineItem[];
  formMachines: ApiMachineItem[];
  formCompanies: string[];
  formAreas: string[];
  showCompany: boolean;
  apiAlarms: AlarmEvent[];
  apiAlarmsError?: boolean;
  selectedAlarm: AlarmEvent | null;
  setSelectedAlarm: (a: AlarmEvent | null) => void;
  offlineCompanies: string[];
  offlineAreas: string[];
  offlineMachineNames: string[];
  reportId?: string | null;
  attachments: Attachment[];
  setAttachments: (a: Attachment[]) => void;
  pendingFiles: File[];
  setPendingFiles: (f: File[]) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel: string;
  editMode?: boolean;
  submitting?: boolean;
}

function ReportFormModal({
  title, formState, setFormState,
  apiMachines, formMachines, formCompanies, formAreas, showCompany,
  apiAlarms, apiAlarmsError = false, selectedAlarm, setSelectedAlarm,
  offlineCompanies, offlineAreas, offlineMachineNames,
  reportId = null, attachments, setAttachments, pendingFiles, setPendingFiles,
  onSubmit, onCancel, submitLabel, editMode = false, submitting = false,
}: ReportFormModalProps) {
  const offline = formState.kind === 'offline';
  const dbNames = [...new Set(apiMachines.map(m => m.db_name).filter(Boolean))];

  /**
   * Flip between an online and an offline report.
   *
   * Clears ONLY the hierarchy fields — severity, analysis, observations,
   * recommendations and any pasted plots survive. The two hierarchies are not
   * transferable, but wiping an analyst's typed narrative because they clicked a
   * toggle is how a feature gets distrusted in its first week.
   */
  const setKind = (kind: 'online' | 'offline') => {
    if (kind === formState.kind) return;
    setFormState({
      ...formState,
      kind,
      plant: '', company: '', area: '', machine: '',
      offlineCompany: '', offlineArea: '', offlineMachine: '',
      alarm: '',
    });
    setSelectedAlarm(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-gray-900 flex items-center gap-3">
            <FileText className="w-6 h-6 text-gray-700" />
            {offline ? title.replace('New Report', 'New Offline Report') : title}
          </h2>
          <div className="flex items-center gap-2">
            {/* Top-right toggle. Hidden in edit mode: a report cannot change what
                it is about, which is the same reason ReportUpdate carries no
                report_kind field. */}
            {!editMode && (
              <button
                type="button"
                onClick={() => setKind(offline ? 'online' : 'offline')}
                title={offline
                  ? 'Switch back to a sensor-based report'
                  : 'Manual route data — enter the machine by hand'}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                  offline
                    ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <WifiOff className="w-4 h-4" />
                Offline
              </button>
            )}
            <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">

          {offline && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <WifiOff className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Offline report — for manual route collection. Type the machine
                details, or pick from ones already used. There is no sensor, so no
                alarm can be linked.
              </span>
            </div>
          )}

          {/* ---------------- ONLINE hierarchy ---------------- */}
          {!offline && (
            <div className={`grid grid-cols-1 gap-4 ${showCompany ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
              <div>
                <label className="block text-gray-700 mb-2 text-sm">Database</label>
                <select
                  value={formState.plant}
                  disabled={editMode}
                  onChange={e => setFormState({ ...formState, plant: e.target.value, company: '', area: '', machine: '' })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                >
                  <option value="">Select</option>
                  {dbNames.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              {showCompany && (
                <div>
                  <label className="block text-gray-700 mb-2 text-sm">Company</label>
                  <select
                    value={formState.company}
                    disabled={editMode}
                    onChange={e => setFormState({ ...formState, company: e.target.value, area: '', machine: '' })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                  >
                    <option value="">Select</option>
                    {formCompanies.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-gray-700 mb-2 text-sm">Area</label>
                <select
                  value={formState.area}
                  disabled={editMode}
                  onChange={e => setFormState({ ...formState, area: e.target.value, machine: '' })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                >
                  <option value="">Select</option>
                  {formAreas.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 mb-2 text-sm">Machine</label>
                <select
                  value={formState.machine}
                  disabled={editMode}
                  onChange={e => setFormState({ ...formState, machine: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                >
                  <option value="">Select</option>
                  {formMachines.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* ---------------- OFFLINE hierarchy ---------------- */}
          {offline && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-gray-700 mb-2 text-sm">Database</label>
                  <select
                    value={formState.plant}
                    disabled={editMode}
                    onChange={e => setFormState({ ...formState, plant: e.target.value, offlineCompany: '', offlineArea: '', offlineMachine: '' })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                  >
                    <option value="">Select</option>
                    {dbNames.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                {/* Company / Area / Machine are comboboxes: pick one already used,
                    or type a new one. The create endpoint is idempotent on the
                    four values, so typing an existing name reuses its row rather
                    than making a duplicate. */}
                <div>
                  <label className="block text-gray-700 mb-2 text-sm">Company</label>
                  <input
                    list="offline-companies"
                    value={formState.offlineCompany}
                    disabled={editMode}
                    onChange={e => setFormState({ ...formState, offlineCompany: e.target.value })}
                    placeholder="Type or pick"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                  />
                  <datalist id="offline-companies">
                    {offlineCompanies.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-gray-700 mb-2 text-sm">Area</label>
                  <input
                    list="offline-areas"
                    value={formState.offlineArea}
                    disabled={editMode}
                    onChange={e => setFormState({ ...formState, offlineArea: e.target.value })}
                    placeholder="Type or pick"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                  />
                  <datalist id="offline-areas">
                    {offlineAreas.map(a => <option key={a} value={a} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-gray-700 mb-2 text-sm">Machine</label>
                  <input
                    list="offline-machines"
                    value={formState.offlineMachine}
                    disabled={editMode}
                    onChange={e => setFormState({ ...formState, offlineMachine: e.target.value })}
                    placeholder="Type or pick"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                  />
                  <datalist id="offline-machines">
                    {offlineMachineNames.map(m => <option key={m} value={m} />)}
                  </datalist>
                </div>
              </div>
              {/* The premise that lets the Database list come from /machines is
                  "no client is offline-only". If that ever stops being true this
                  says so, instead of rendering an empty select. */}
              {!editMode && dbNames.length === 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    No databases are available to you. Offline reports still need a
                    database to file under, and this list comes from the machines
                    you can see.
                  </span>
                </div>
              )}
            </>
          )}

          {/* Severity */}
          <div>
            <label className="block text-gray-700 mb-2 text-sm">Severity</label>
            <select value={formState.severity} onChange={e => setFormState({ ...formState, severity: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="1">1 — Immediate Action</option>
              <option value="2">2 — Schedule Action</option>
              <option value="3">3 — Trend Closely</option>
              <option value="4">4 — Trend Normally</option>
              <option value="5">5 — No fault found, alarm adjusted</option>
              <option value="6">6 — No fault found, no action taken</option>
            </select>
          </div>

          {/* Analysis */}
          <div>
            <label className="block text-gray-700 mb-2 text-sm">Analysis</label>
            <select value={formState.analysis} onChange={e => setFormState({ ...formState, analysis: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="">Select Analysis Type</option>
              {ANALYSIS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Linked alarm — online, create only. An offline asset has no sensor. */}
          {!editMode && !offline && formState.machine && apiAlarmsError && (
            <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Alarm history could not be loaded — cannot link an alarm right now.
            </div>
          )}
          {!editMode && !offline && formState.machine && !apiAlarmsError && apiAlarms.length > 0 && (
            <div>
              <label className="block text-gray-700 mb-2 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                Link to Alarm (optional)
              </label>
              <select value={formState.alarm}
                onChange={e => {
                  setFormState({ ...formState, alarm: e.target.value });
                  setSelectedAlarm(apiAlarms.find(a => a.id === e.target.value) || null);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                <option value="">None</option>
                {apiAlarms.map(alarm => {
                  // F-18: peak_value is raw mm/s. When relabeling velocity to in/s,
                  // convert the number too (mm/s ÷ 25.4) so it matches the unit.
                  const isVel = alarm.metric === 'Velocity';
                  const unit = isVel ? 'in/s' : alarm.metric === 'Temperature' ? '°F' : "G's";
                  const shownValue = isVel ? (alarm.value / 25.4).toFixed(3) : alarm.value;
                  return (
                    <option key={alarm.id} value={alarm.id}>
                      {fmtEastern(alarm.timestamp)} — {alarm.pointName} {alarm.metric} ({shownValue} {unit})
                    </option>
                  );
                })}
              </select>
              {selectedAlarm && (
                <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-gray-700">
                  <span className={`font-semibold ${selectedAlarm.severity === 'high' ? 'text-red-600' : 'text-yellow-600'}`}>
                    {selectedAlarm.severity === 'high' ? 'Critical' : 'Warning'}:
                  </span> {selectedAlarm.description}
                  <p className="text-gray-500 mt-1">Occurred on {fmtEastern(selectedAlarm.timestamp)}</p>
                </div>
              )}
            </div>
          )}

          {/* Observations */}
          <div>
            <label className="block text-gray-700 mb-2 text-sm">Observations</label>
            <textarea value={formState.observations} onChange={e => setFormState({ ...formState, observations: e.target.value })}
              placeholder="Enter observations…" rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm" />
          </div>

          {/* Recommendations */}
          <div>
            <label className="block text-gray-700 mb-2 text-sm">Recommendations</label>
            <textarea value={formState.recommendations} onChange={e => setFormState({ ...formState, recommendations: e.target.value })}
              placeholder="Enter recommendations…" rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm" />
          </div>

          <PlotEditor
            plots={formState.plotImages}
            setPlots={(plotImages: PlotImage[]) => setFormState({ ...formState, plotImages })}
          />

          <AttachmentEditor
            reportId={reportId}
            existing={attachments}
            setExisting={setAttachments}
            pending={pendingFiles}
            setPending={setPendingFiles}
          />
        </div>
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button onClick={onCancel} disabled={submitting} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50">Cancel</button>
          <button
            onClick={onSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:bg-blue-300 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
